import React, { useMemo, useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import "./PhotoViewer.css";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

export default function PhotoViewer() {
  const location = useLocation();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const photosParam = searchParams.get("photos");
  const initialPhotoIndex = parseInt(searchParams.get("photo") || "0", 10);

  const slides = useMemo(() => {
    if (!photosParam) {
      console.log("PhotoViewer: No photos parameter found");
      return [];
    }

    try {
      let parsed;
      try {
        parsed = JSON.parse(photosParam);
      } catch (directParseError) {
        try {
          let decoded = decodeURIComponent(photosParam);
          console.log("PhotoViewer: Decoded photos param:", decoded);
          parsed = JSON.parse(decoded);
          console.log("PhotoViewer: Successfully parsed after single decode");
        } catch (singleDecodeError) {
          console.log(
            "PhotoViewer: Single decode failed, trying double decode..."
          );

          // Strategy 3: Try double decoding (for double-encoded URLs)
          try {
            let decoded = decodeURIComponent(photosParam);
            decoded = decodeURIComponent(decoded);
            console.log("PhotoViewer: Double decoded photos param:", decoded);
            parsed = JSON.parse(decoded);
            console.log("PhotoViewer: Successfully parsed after double decode");
          } catch (doubleDecodeError) {
            console.error("PhotoViewer: All parse attempts failed");
            console.error(
              "PhotoViewer: Direct parse error:",
              directParseError.message
            );
            console.error(
              "PhotoViewer: Single decode error:",
              singleDecodeError.message
            );
            console.error(
              "PhotoViewer: Double decode error:",
              doubleDecodeError.message
            );
            throw directParseError;
          }
        }
      }

      if (!Array.isArray(parsed)) {
        console.error(
          "PhotoViewer: Parsed data is not an array:",
          typeof parsed
        );
        return [];
      }

      const slides = parsed.map((p) => ({
        src: p.url,
        alt: p.caption || "",
        rotation: typeof p.rotation === "number" ? p.rotation : 0,
      }));
      return slides;
    } catch (err) {
      console.error("PhotoViewer: failed to parse photos param", err);
      console.error("PhotoViewer: Raw param was:", photosParam);
      return [];
    }
  }, [photosParam]);

  const [open, setOpen] = useState(true);
  const [index, setIndex] = useState(initialPhotoIndex || 0);
  const [rotation, setRotation] = useState(
    slides[initialPhotoIndex]?.rotation || 0
  );

  const [zoom, setZoom] = useState(
    slides[initialPhotoIndex]?.rotation === 90 ||
      slides[initialPhotoIndex]?.rotation === -90 ||
      slides[initialPhotoIndex]?.rotation === 270 ||
      slides[initialPhotoIndex]?.rotation === -270
      ? 2
      : 1.2
  );
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const isDragging = useRef(false);
  const lastPos = useRef({ x: 0, y: 0 });
  const imgRef = useRef(null);
  const containerRef = useRef(null);

  const touchStartDist = useRef(null);

  const calculateZoom = (imageSrc, imageRotation) => {
    const container = containerRef.current;
    if (!container) return;
    // Create a new image to get dimensions immediately
    const img = new Image();
    img.onload = () => {
      // Always use window dimensions
      const containerWidth = window.innerWidth * 0.9; // 90vw
      const containerHeight = window.innerHeight * 0.9; // 90vh

      let naturalWidth = img.naturalWidth;
      let naturalHeight = img.naturalHeight;

      const isRotated = [90, -90, 270, -270].includes(imageRotation);

      const rotatedWidth = isRotated ? naturalHeight : naturalWidth;
      const rotatedHeight = isRotated ? naturalWidth : naturalHeight;

      const widthScale = containerWidth / rotatedWidth;
      const heightScale = containerHeight / rotatedHeight;

      console.log("widthScale:", widthScale);
      console.log("heightScale:", heightScale);

      let fitScale = isRotated ? Math.min(heightScale, widthScale) : 1.2;
      fitScale = isRotated
        ? Math.max(fitScale + 0.8, 1.2)
        : Math.min(fitScale + 0.2, 1.2);
      console.log("auto zoom:", fitScale.toFixed(2));
      setZoom(fitScale);
      setOffset({ x: 0, y: 0 });
    };

    // Set the source to trigger the onload
    img.src = imageSrc;
  };

  useEffect(() => {
    setIndex(initialPhotoIndex || 0);
    setRotation(slides[initialPhotoIndex]?.rotation || 0);
    if (slides[initialPhotoIndex]) {
      calculateZoom(
        slides[initialPhotoIndex].src,
        slides[initialPhotoIndex].rotation || 0
      );
    }
    setOffset({ x: 0, y: 0 });
  }, [initialPhotoIndex, slides]);

  const handlePrev = () => {
    const newIndex = index > 0 ? index - 1 : slides.length - 1;
    setIndex(newIndex);
    setRotation(slides[newIndex]?.rotation || 0);
    setOffset({ x: 0, y: 0 });
    // Calculate zoom immediately with the image source and rotation
    calculateZoom(slides[newIndex].src, slides[newIndex]?.rotation || 0);
  };

  const handleNext = () => {
    const newIndex = index < slides.length - 1 ? index + 1 : 0;
    setIndex(newIndex);
    setRotation(slides[newIndex]?.rotation || 0);
    setOffset({ x: 0, y: 0 });
    // Calculate zoom immediately with the image source and rotation
    calculateZoom(slides[newIndex].src, slides[newIndex]?.rotation || 0);
  };

  const handleThumbnailClick = (i) => {
    setIndex(i);
    setRotation(slides[i]?.rotation || 0);
    setOffset({ x: 0, y: 0 });
    // Calculate zoom immediately with the image source and rotation
    calculateZoom(slides[i].src, slides[i]?.rotation || 0);
  };

  const handleZoomIn = () => setZoom((z) => Math.min(z + 0.2, 3));
  const handleZoomOut = () => setZoom((z) => Math.max(z - 0.2, 0.2));

  const handleWheel = (e) => {
    if (e.deltaY < 0) {
      setZoom((z) => Math.min(z + 0.1, 3));
    } else {
      setZoom((z) => Math.max(z - 0.1, 0.2));
    }
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      touchStartDist.current = dist;
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && touchStartDist.current) {
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      const delta = dist - touchStartDist.current;

      if (Math.abs(delta) > 5) {
        if (delta > 0) {
          setZoom((z) => Math.min(z + 0.05, 3));
        } else {
          setZoom((z) => Math.max(z - 0.05, 0.2));
        }
        touchStartDist.current = dist;
      }
    }

    if (e.touches.length === 1) {
      e.stopPropagation();
    }
  };

  const handleTouchEnd = () => {
    touchStartDist.current = null;
  };

  const handleMouseDown = (e) => {
    isDragging.current = true;
    lastPos.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseMove = (e) => {
    if (isDragging.current) {
      const dx = e.clientX - lastPos.current.x;
      const dy = e.clientY - lastPos.current.y;
      setOffset((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
      lastPos.current = { x: e.clientX, y: e.clientY };
    }
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  useEffect(() => {
    if (!open) return;

    const keyHandler = (e) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        e.stopPropagation();
        handlePrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        e.stopPropagation();
        handleNext();
      }
    };

    window.addEventListener("keydown", keyHandler, true);
    return () => {
      window.removeEventListener("keydown", keyHandler, true);
    };
  }, [open, index, slides]);

  if (!slides || slides.length === 0) {
    return <div style={{ padding: 20 }}>No photos to display.</div>;
  }

  return (
    <Lightbox
      open={open}
      close={() => {
        setOpen(false);
        window.close?.();
      }}
      slides={slides}
      index={index}
      controller={{
        closeOnBackdropClick: true,
        closeOnPullDown: false,
        keyboardNavigation: false,
        disableSwipeNavigation: true,
      }}
      render={{
        iconPrev: () => null,
        iconNext: () => null,
        slide: ({ slide }) => (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              width: "100%",
            }}
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => {
              if (e.touches && e.touches.length === 1) e.stopPropagation();
            }}
            onPointerDown={(e) => e.stopPropagation()}
            onPointerMove={(e) => e.stopPropagation()}
          >
            <div
              ref={containerRef}
              className="photo-main"
              onWheel={handleWheel}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <img
                key={index}
                ref={imgRef}
                src={slide.src}
                alt={slide.alt || ""}
                style={{
                  maxWidth: "90vw",
                  maxHeight: "70vh",
                  transform: `translate(${offset.x}px, ${offset.y}px) rotate(${rotation}deg) scale(${zoom})`,
                  transformOrigin: "center center",
                  cursor: "grab",
                  userSelect: "none",
                  display: "block",
                  marginTop: "-50px",
                }}
                draggable={false}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              />
            </div>

            <div className="photo-toolbar">
              <button
                className="btn btn-light btn-sm"
                onClick={handleZoomIn}
                title="Zoom In"
              >
                <AddIcon />
              </button>
              <button
                className="btn btn-light btn-sm"
                onClick={handleZoomOut}
                title="Zoom Out"
              >
                <RemoveIcon style={{ transform: "rotate(180deg)" }} />
              </button>
              <button
                className="btn btn-light btn-sm"
                onClick={handlePrev}
                title="Previous"
              >
                <ArrowBackIcon />
              </button>
              <button
                className="btn btn-light btn-sm"
                onClick={handleNext}
                title="Next"
              >
                <ArrowForwardIcon />
              </button>
            </div>

            <div className="photo-thumbnails">
              {slides.map((s, i) => (
                <img
                  key={i}
                  src={s.src}
                  alt={s.alt}
                  className={`thumbnail ${i === index ? "active" : ""}`}
                  onClick={() => handleThumbnailClick(i)}
                  draggable={false}
                />
              ))}
            </div>
          </div>
        ),
      }}
    />
  );
}
